import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Button,
  Chip,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DashboardLayout from '../components/DashboardLayout';
import { useNavigate } from 'react-router-dom';
import { getServices, deleteService } from '../services/serviceService';
import { resolveAssetUrl } from '../utils/assetUrl';
import AdminEmptyState from '../components/AdminEmptyState';
import AdminPageHeader from '../components/AdminPageHeader';
import AdminPagination from '../components/AdminPagination';
import AdminSection from '../components/AdminSection';
import useAdminPagination from '../hooks/useAdminPagination';

function ServicesPage() {
  const [services, setServices] = useState([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const {
    page,
    rowsPerPage,
    count,
    paginatedItems: paginatedServices,
    handlePageChange,
    handleRowsPerPageChange,
  } = useAdminPagination(services, 10);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const data = await getServices();
      setServices(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching services:', error);
      setServices([]);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this service?')) {
      return;
    }

    try {
      await deleteService(id);
      fetchServices();
    } catch (error) {
      console.error('Error deleting service:', error);
    }
  };

  return (
    <DashboardLayout>
      <Stack spacing={3}>
        <AdminPageHeader
          eyebrow="Operations"
          title="Services"
          description="Keep the service catalog clean, current, and easy to maintain."
          meta={<Chip label={`${services.length} services`} variant="outlined" />}
          action={
            <Button variant="contained" onClick={() => navigate('/services/create')}>
              Add Service
            </Button>
          }
        />

        {isMobile ? (
          services.length ? (
            <Grid container spacing={2}>
              {paginatedServices.map((service) => (
                <Grid item xs={12} key={service.id}>
                  <AdminSection>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar
                        src={service.imageUrl ? resolveAssetUrl(service.imageUrl) : undefined}
                        variant="rounded"
                        sx={{ width: 56, height: 56, borderRadius: 2 }}
                      >
                        {service.name?.charAt(0)}
                      </Avatar>
                      <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0.5}>
                        <Typography variant="h6">{service.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {service.slug}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1}>
                        <Button variant="outlined" onClick={() => navigate(`/services/edit/${service.id}`)}>
                          Edit
                        </Button>
                        <IconButton color="error" onClick={() => handleDelete(service.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </AdminSection>
                </Grid>
              ))}
            </Grid>
          ) : (
            <AdminSection>
              <AdminEmptyState
                title="No services added yet"
                description="Start by creating the first service entry for the public site."
              />
            </AdminSection>
          )
        ) : (
          <AdminSection sx={{ p: 0, overflow: 'hidden' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Service</TableCell>
                  <TableCell>Slug</TableCell>
                  <TableCell>Image</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {services.length ? (
                  paginatedServices.map((service) => (
                    <TableRow key={service.id}>
                      <TableCell sx={{ fontWeight: 600 }}>{service.name}</TableCell>
                      <TableCell>{service.slug || '-'}</TableCell>
                      <TableCell>
                        <Avatar
                          src={service.imageUrl ? resolveAssetUrl(service.imageUrl) : undefined}
                          variant="rounded"
                          sx={{ width: 44, height: 44, borderRadius: 2 }}
                        >
                          {service.name?.charAt(0)}
                        </Avatar>
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          variant="outlined"
                          onClick={() => navigate(`/services/edit/${service.id}`)}
                          sx={{ mr: 1 }}
                        >
                          Edit
                        </Button>
                        <IconButton color="error" onClick={() => handleDelete(service.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <AdminEmptyState
                        title="No services added yet"
                        description="Start by creating the first service entry for the public site."
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <AdminPagination
              count={count}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </AdminSection>
        )}
      </Stack>
    </DashboardLayout>
  );
}

export default ServicesPage;
